// MenuNavLeft.js
import React, { useState } from "react";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import ProfileMenu from "./components/ProfileMenu";
import CustomInstallBanner from "./CustomInstallBanner";
import { isAdminRole, isAdminOrManagerRole } from "./utils/userRoles";
import { FEATURES } from "./config/features";
import {
  Sidebar,
  SidebarLogo,
  SidebarProfileCard,
  SidebarNav,
  SidebarSection,
  SidebarItem,
  SidebarGroup,
  SidebarBadge,
  SidebarFooter,
} from "./components/ui";

import DashboardIcon from "@mui/icons-material/Dashboard";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import SearchIcon from "@mui/icons-material/Search";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import CampaignIcon from "@mui/icons-material/Campaign";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContactsIcon from "@mui/icons-material/Contacts";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import TelegramIcon from "@mui/icons-material/Telegram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssessmentIcon from "@mui/icons-material/Assessment";

export default function MenuNavLeft({ user, unreadCount, updatedCount, handleLogout }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const [openEmail, setOpenEmail] = useState(false);
  const [openSMS, setOpenSMS] = useState(false);
  const [openParsing, setOpenParsing] = useState(false);

  React.useEffect(() => {
    if (
      currentPath.startsWith("/campaign-dashboard") ||
      currentPath.startsWith("/campaigns") ||
      currentPath.startsWith("/templates") ||
      currentPath.startsWith("/contacts") ||
      currentPath.startsWith("/emails") ||
      currentPath.startsWith("/domains")
    ) {
      setOpenEmail(true);
    }
    if (currentPath.startsWith("/sms/")) setOpenSMS(true);
    if (currentPath.startsWith("/parsing")) setOpenParsing(true);
  }, [currentPath]);

  const isActive = (path) => {
    if (path === "/dashboard" && currentPath === "/dashboard") return true;
    if (path === "/requests" && currentPath === "/requests") return true;
    if (path === "/admin" && currentPath === "/admin") return true;
    if (path === "/mobile-monitoring" && currentPath === "/mobile-monitoring") return true;
    return currentPath === path;
  };

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarProfileCard>
        <ProfileMenu user={user} handleLogout={handleLogout} navTone="light" />
      </SidebarProfileCard>

      <SidebarNav>
        <SidebarItem to="/dashboard" icon={DashboardIcon} label="Dashboard" active={isActive("/dashboard")} />

        <SidebarGroup
          label="Email Marketing"
          icon={EmailIcon}
          open={openEmail}
          onToggle={() => setOpenEmail(!openEmail)}
        >
          <SidebarItem to="/campaign-dashboard" icon={DashboardCustomizeIcon} label="Campaigns" active={isActive("/campaign-dashboard")} sub />
          <SidebarItem to="/campaigns" icon={CampaignIcon} label="Create Campaign" active={isActive("/campaigns")} sub />
          <SidebarItem to="/templates" icon={ContentCopyIcon} label="Templates" active={isActive("/templates")} sub />
          <SidebarItem to="/contacts" icon={ContactsIcon} label="Contact Groups" active={isActive("/contacts")} sub />
          <SidebarItem to="/subscribe-forms" icon={ContentCopyIcon} label="Subscribe Forms" active={isActive("/subscribe-forms")} sub />
          <SidebarItem to="/emails" icon={MailOutlineIcon} label="Verified Emails" active={isActive("/emails")} sub />
          <SidebarItem to="/domains" icon={LanguageIcon} label="Verified Domains" active={isActive("/domains")} sub />
        </SidebarGroup>

        <SidebarGroup
          label="SMS Marketing"
          icon={SmsIcon}
          open={openSMS}
          onToggle={() => setOpenSMS(!openSMS)}
        >
          <SidebarItem to="/sms/dashboard" icon={DashboardCustomizeIcon} label="Campaigns" active={isActive("/sms/dashboard")} sub />
          <SidebarItem to="/sms/campaign" icon={CampaignIcon} label="Create Campaign" active={isActive("/sms/campaign")} sub />
          <SidebarItem to="/sms/contacts" icon={ContactsIcon} label="Contact Groups" active={isActive("/sms/contacts")} sub />
          <SidebarItem to="/sms/phones" icon={PhoneAndroidIcon} label="Verified Phones" active={isActive("/sms/phones")} sub />
        </SidebarGroup>
      </SidebarNav>

      <SidebarSection>
        {FEATURES.PROJECTS_ENABLED && (
          <SidebarItem
            to="/requests"
            icon={RequestQuoteIcon}
            label="Projects"
            active={isActive("/requests")}
            badges={
              <Box sx={{ display: "flex", gap: 1 }}>
                {unreadCount > 0 && <SidebarBadge content={unreadCount} />}
                {updatedCount > 0 && <SidebarBadge content={updatedCount} tone="secondary" />}
              </Box>
            }
          />
        )}

        {isAdminOrManagerRole(user?.role) && (
          <SidebarGroup
            label="Parsing"
            icon={SearchIcon}
            open={openParsing}
            onToggle={() => setOpenParsing(!openParsing)}
          >
            {isAdminRole(user?.role) && (
              <SidebarItem to="/parsing/telegram" icon={TelegramIcon} label="Telegram" active={isActive("/parsing/telegram")} sub />
            )}
            {isAdminRole(user?.role) && (
              <SidebarItem to="/parsing/whatsapp" icon={WhatsAppIcon} label="WhatsApp" active={isActive("/parsing/whatsapp")} sub />
            )}
            {isAdminRole(user?.role) && (
              <SidebarItem to="/parsing/viber" icon={PhoneIphoneIcon} label="Viber" active={isActive("/parsing/viber")} sub />
            )}
            <SidebarItem to="/parsing" icon={ContactMailIcon} label="Parsing contacts" active={isActive("/parsing")} sub />
          </SidebarGroup>
        )}

        {isAdminRole(user?.role) && (
          <SidebarItem to="/admin" icon={AdminPanelSettingsIcon} label="User Management" active={isActive("/admin")} />
        )}
        {isAdminOrManagerRole(user?.role) && (
          <SidebarItem to="/mobile-monitoring" icon={AssessmentIcon} label="System Monitoring" active={isActive("/mobile-monitoring")} />
        )}
      </SidebarSection>

      <SidebarFooter>
        <CustomInstallBanner />
      </SidebarFooter>
    </Sidebar>
  );
}
